import { useTranslation } from 'react-i18next';

interface Props {
  layerName: string;
  min: number;
  max: number;
  gradientName?: string;
  unit?: string;
}

// Hand-rolled CSS gradients matching the @pnext/three-loader gradient names
// the SkyGIS layer settings dialog exposes (SPECTRAL, RAINBOW, VIRIDIS, PLASMA,
// INFERNO, GRAYSCALE, YELLOW_GREEN). Approximate; viewer is the source of truth.
const GRADIENT_CSS: Record<string, string> = {
  SPECTRAL: 'linear-gradient(to right, #3288bd, #66c2a5, #abdda4, #e6f598, #ffffbf, #fee08b, #fdae61, #f46d43, #d53e4f)',
  RAINBOW: 'linear-gradient(to right, #00007f, #0000ff, #007fff, #00ffff, #7fff7f, #ffff00, #ff7f00, #ff0000, #7f0000)',
  VIRIDIS: 'linear-gradient(to right, #440154, #3b528b, #21918c, #5ec962, #fde725)',
  PLASMA: 'linear-gradient(to right, #0d0887, #6a00a8, #b12a90, #e16462, #fca636, #f0f921)',
  INFERNO: 'linear-gradient(to right, #000004, #420a68, #932667, #dd513a, #fca50a, #fcffa4)',
  GRAYSCALE: 'linear-gradient(to right, #000, #888, #fff)',
  YELLOW_GREEN: 'linear-gradient(to right, #ffffe5, #f7fcb9, #addd8e, #41ab5d, #006837)',
};

export function DeviationColorbar({ layerName, min, max, gradientName = 'SPECTRAL', unit }: Props) {
  const { t } = useTranslation('project');
  const gradient = GRADIENT_CSS[gradientName] ?? GRADIENT_CSS.SPECTRAL;

  // Choose units automatically when not explicitly provided. Survey deviations
  // are typically sub-metre (centimetres or millimetres); showing "+0.050 m"
  // wastes precision and reads worse than "+5 cm". Switch to centimetres when
  // the largest absolute end of the range is < 0.5 m, and to millimetres
  // when it's < 0.005 m.
  const absMax = Math.max(Math.abs(min), Math.abs(max), 1e-9);
  let scale = 1;
  let resolvedUnit = unit ?? 'm';
  if (unit === undefined) {
    if (absMax < 0.005) {
      scale = 1000;
      resolvedUnit = 'mm';
    } else if (absMax < 0.5) {
      scale = 100;
      resolvedUnit = 'cm';
    }
  }

  const scaledMin = min * scale;
  const scaledMax = max * scale;
  const scaledAbs = absMax * scale;
  // Pick decimals to keep ~2 significant figures around the larger end.
  // e.g. ±5 cm → "5.00" (2 decimals); ±50 mm → "50" (0 decimals).
  const decimals = Math.max(0, 2 - Math.floor(Math.log10(Math.max(scaledAbs, 1e-9))));
  const fmt = (v: number) => v.toFixed(decimals);

  // Visual position of the "0" tick on the colorbar. The shader maps values
  // linearly across [min, max], so 0 is NOT at the visual centre when the
  // range is asymmetric — it sits at (0 − min) / (max − min) of the way.
  // Mis-rendering this caused real confusion: with min=-10, max=+1, a
  // 0.01 m point lands at 91 % of the gradient (very red), not near the
  // visual centre as the old "left/centre/right" legend implied.
  const zeroFracRaw = max > min ? (0 - min) / (max - min) : 0.5;
  const zeroFrac = Math.min(0.96, Math.max(0.04, zeroFracRaw));
  const zeroInRange = min < 0 && max > 0;

  return (
    <div className='rounded-lg shadow-lg px-3 py-2 backdrop-blur bg-white/90 dark:bg-stone-900/90 min-w-48'>
      <div className='text-xs text-slate-700 dark:text-stone-200 font-medium truncate mb-1' title={layerName}>
        {layerName}
      </div>
      <div className='text-[10px] text-slate-500 dark:text-stone-400 mb-1'>
        {t('analyses.deviationLegend', { defaultValue: 'Deviation' })} ({resolvedUnit})
      </div>
      <div className='relative w-full h-3 rounded' style={{ background: gradient }}>
        {zeroInRange && (
          <div
            className='absolute top-0 h-full w-px bg-slate-900/70 dark:bg-white/70'
            style={{ left: `${zeroFrac * 100}%` }}
            aria-hidden
          />
        )}
      </div>
      <div className='relative h-4 mt-1 text-[11px] text-slate-700 dark:text-stone-300'>
        <span className='absolute left-0'>{fmt(scaledMin)}</span>
        {zeroInRange && (
          <span
            className='absolute -translate-x-1/2'
            style={{ left: `${zeroFrac * 100}%` }}
          >
            0
          </span>
        )}
        <span className='absolute right-0'>+{fmt(scaledMax)}</span>
      </div>
    </div>
  );
}
